import { Component, type ReactNode } from 'react';

interface Props { children: ReactNode; }
interface State { hasError: boolean; }

/**
 * Catches dynamic import failures (ChunkLoadError) that happen when a new
 * deployment invalidates old chunk URLs still referenced by a cached index.js.
 * Reloads the page once so the fresh index.html is fetched with correct URLs.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  private reloaded = false;

  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      /loading.*chunk/i.test(error.message) ||
      /failed.*import/i.test(error.message) ||
      /dynamically imported module/i.test(error.message);

    if (isChunkError && !this.reloaded) {
      this.reloaded = true;
      window.location.reload();
    }
  }

  render() {
    // While waiting for reload, show nothing (avoids flash of error UI)
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
